import type { Org } from '@/features/org/api';

/*
 * The signup wizard's steps, in order, and the server-side journey that resumes them
 * (ported from the legacy dashboard's flowSteps.ts and utils/onboardingJourney.ts).
 * The server is the only source of truth: `organizations.metadata.onboarding`.
 */
export type FlowStepId =
  | 'verify-email'
  | 'connect-data'
  | 'business-dna'
  | 'personalization'
  | 'about-company'
  | 'referral'
  | 'ai-visibility';

export type OnboardingJourneyStep = FlowStepId | 'completed';

export interface OnboardingJourney {
  step?: OnboardingJourneyStep;
  /** step → ISO time the user first reached it. */
  steps?: Record<string, string>;
}

export interface FlowStep {
  id: FlowStepId;
  label: string;
}

export const FLOW_STEPS: readonly FlowStep[] = [
  { id: 'verify-email', label: 'Verify Email' },
  { id: 'connect-data', label: 'Connect Data' },
  { id: 'business-dna', label: 'Business DNA' },
  { id: 'personalization', label: 'Personalization' },
  { id: 'about-company', label: 'About Company' },
  { id: 'referral', label: 'Referral or Promo Code' },
  { id: 'ai-visibility', label: 'AI Visibility' },
];

export type StepRoute =
  | '/onboarding/connect-data'
  | '/onboarding/business-dna'
  | '/onboarding/personalization'
  | '/onboarding/about-company'
  | '/onboarding/referral-code'
  | '/onboarding/ai-visibility';

/**
 * Step → page. A verified user whose org still says 'verify-email' continues at Connect
 * Data (legacy's VerifyEmail forwarded verified users there; /verify-email itself turns a
 * verified user away, so sending them to it would loop).
 */
export const ONBOARDING_STEP_ROUTES: Record<FlowStepId, StepRoute> = {
  'verify-email': '/onboarding/connect-data',
  'connect-data': '/onboarding/connect-data',
  'business-dna': '/onboarding/business-dna',
  personalization: '/onboarding/personalization',
  'about-company': '/onboarding/about-company',
  referral: '/onboarding/referral-code',
  'ai-visibility': '/onboarding/ai-visibility',
};

/** The legacy Mixpanel step names, kept for the funnel. */
export const STEP_EVENT: Record<FlowStepId, string> = {
  'verify-email': 'verify_email',
  'connect-data': 'connect_data',
  'business-dna': 'business_dna',
  personalization: 'personalization',
  'about-company': 'about_company',
  referral: 'referral',
  'ai-visibility': 'ai_visibility',
};

type OrgLike = Pick<Org, 'onboarding_completed' | 'metadata'> | null | undefined;

const isStep = (v: unknown): v is FlowStepId => typeof v === 'string' && v in ONBOARDING_STEP_ROUTES;

export function getOnboardingJourney(org: OrgLike): OnboardingJourney | null {
  const journey = org?.metadata?.['onboarding'];
  return journey && typeof journey === 'object' ? journey : null;
}

/**
 * The page an in-progress user is sent to, or null when there is nothing to resume.
 * Deliberately null when the journey key is absent: older orgs with
 * onboarding_completed=false that predate step tracking are never pulled into the flow.
 * An unknown step fails open.
 */
export function getServerOnboardingRoute(org: OrgLike): StepRoute | null {
  if (!org || org.onboarding_completed) return null;
  const step = getOnboardingJourney(org)?.step;
  if (!step || step === 'completed') return null;
  return isStep(step) ? ONBOARDING_STEP_ROUTES[step] : null;
}

export function stepIndex(id: FlowStepId): number {
  return FLOW_STEPS.findIndex((s) => s.id === id);
}
